import { Dropdown } from "antd";
import { useState, useEffect, useCallback, useId, useRef } from "react";
import {
  CascaderMenuToggler,
  StyledCascaderMenu,
  StyledCascaderLabel,
} from "./styles";
import arrowDownBlueSm from "@/assets/arrow-down-blue-sm.svg";

const CascaderMenu = ({
  items,
  placeholder = "Select an item",
  label = "",
  trigger = "click", // 'click' | 'hover' | 'contextMenu'
  arrow = false,
  disabled = false,
  iconOnlyMenu = false,
  open = null,
  onOpenChange,
  onSelect,
  menuClassname = "",
  togglerClass = "",
  placement = "bottomLeft",
  showSelectedLabel = true,
  togglerIcon = <img className="arrow-img" src={arrowDownBlueSm} />,
  defaultValue = null, // Accept a default key
}) => {
  const [selectedLabel, setSelectedLabel] = useState(placeholder);
  const [menuMaxHeight, setMenuMaxHeight] = useState("60vh");
  const rawPopupId = useId();
  const popupMarkerClass = `cascader-popup-${rawPopupId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const togglerRef = useRef(null);
  const MARGIN = 16;

  // antd normally anchors this popup to the toggler by its BOTTOM edge
  // (position: absolute; bottom: <n>px) and grows it upward — the bottom
  // edge doesn't move regardless of how tall we let the popup be. But when
  // the toggler sits too close to the top of the viewport for that (e.g. a
  // sidebar box with nothing above it), antd's own overflow handling flips
  // the popup to open downward instead, anchored by its TOP edge instead —
  // and now it's the top edge that stays put while the bottom grows.
  // Predicting which way it'll flip ahead of time proved unreliable, so
  // this is self-correcting either way: let it render with a placeholder
  // height, then check which edge of the popup actually lines up with the
  // toggler (top-of-popup ≈ bottom-of-toggler → opened downward,
  // bottom-of-popup ≈ top-of-toggler → opened upward) and re-cap the height
  // to fit within the viewport on whichever side it grows. One correction
  // pass is enough — no feedback loop.
  const fitMenuToViewport = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const popupEl = document.querySelector(`.${popupMarkerClass}`);
        const togglerRect = togglerRef.current?.getBoundingClientRect();
        if (!popupEl || !togglerRect) return;
        const { top, bottom } = popupEl.getBoundingClientRect();

        const openedDownward =
          Math.abs(top - togglerRect.bottom) <= Math.abs(bottom - togglerRect.top);

        const fitted = openedDownward
          ? `${Math.max(150, window.innerHeight - top - MARGIN)}px`
          : `${Math.max(150, bottom - MARGIN)}px`;

        // The inline style below is what most callers rely on. This
        // specific "dark" cascader menu is also targeted by an `!important`
        // rule in index.css (`.cascader-dropdown-menu.ant-dropdown-menu-dark`)
        // that would otherwise always beat this inline value — that rule
        // reads its height from this CSS variable, so it has to be set too.
        popupEl.style.setProperty("--cascader-menu-max-height", fitted);
        setMenuMaxHeight(fitted);
      });
    });
  }, [popupMarkerClass]);

  const handleOpenChange = useCallback(
    (nextOpen, info) => {
      if (nextOpen) {
        setMenuMaxHeight("60vh");
        fitMenuToViewport();
      }
      if (onOpenChange) onOpenChange(nextOpen, info);
    },
    [fitMenuToViewport, onOpenChange],
  );

  // Recursive function to find an item by key
  const findItemByKey = useCallback((items, key) => {
    for (let item of items) {
      if (item.key === key) {
        return item;
      }
      if (item.children) {
        const found = findItemByKey(item.children, key);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Initialize with default value
  useEffect(() => {
    if (defaultValue) {
      const defaultItem = findItemByKey(items, defaultValue);
      if (defaultItem) {
        setSelectedLabel(defaultItem.label);
      }
    }
  }, [defaultValue, items, findItemByKey]);

  // Handle selection change
  const handleItemClick = useCallback(
    (e) => {
      const selectedItem = findItemByKey(items, e.key);
      if (selectedItem) {
        setSelectedLabel(selectedItem.label);
      }
      if (onSelect) onSelect(e);
    },
    [items, onSelect, findItemByKey],
  );

  return (
    <StyledCascaderMenu disabled={disabled}>
      {label && <StyledCascaderLabel>{label}</StyledCascaderLabel>}

      <Dropdown
        rootClassName={popupMarkerClass}
        menu={{
          rootClassName: `cascader-dropdown-menu ${menuClassname}`,
          items,
          theme: "dark",
          onClick: handleItemClick,
          style: {
            maxHeight: menuMaxHeight,
            overflowY: "auto",
            overflowX: "hidden",
          },
        }}
        destroyPopupOnHide
        open={open}
        trigger={[trigger]}
        disabled={disabled}
        onOpenChange={handleOpenChange}
        placement={placement}
        autoFocus
      >
        <CascaderMenuToggler
          ref={togglerRef}
          onClick={(e) => e.preventDefault()}
          className={`${togglerClass}`}
        >
          {!iconOnlyMenu && (
            <span
              className="selection-text"
              title={showSelectedLabel ? selectedLabel : placeholder}
            >
              {showSelectedLabel ? selectedLabel : placeholder}
            </span>
          )}

          {togglerIcon && <i>{togglerIcon}</i>}
        </CascaderMenuToggler>
      </Dropdown>
    </StyledCascaderMenu>
  );
};

export default CascaderMenu;
